import { useState } from 'react';
import { Close } from '@mui/icons-material';
import { IconButton, TextField, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import TypographyButton from 'further-ui/components/TypographyButton';
import CurrencyRate from 'components/CurrencyRate';
import AllocationsManager from 'components/new/AllocationsManager';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { ActionCommitmentStatus } from 'further-types/lp/action';
import { FundingSource } from 'further-types/lp/investment';
import useFundingSourceOptions from 'hooks/form/useFundingSourceOptions';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import StaticField from 'further-ui/components/StaticField';
import CounterpartySelector from 'components/new/CounterpartySelector';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useCurrencyRate from 'hooks/lp/data/currencies/useCurrencyRate';
import api from 'lib/trpcClient';
import { round } from 'lodash';
import { bankReconciliation as rules } from 'further-ui/validations';
import VatRateSelector from 'components/new/VatRateSelector';

type Props = {
  vehicleId: string;
  currency: CurrencyCode;
  date: CalendarDay;
  onRemove: () => void;
};

const AddBankFeeForm: React.FC<Props> = ({
  vehicleId,
  currency,
  date,
  onRemove,
}) => {
  const [showAllocations, setShowAllocations] = useState(false);
  const { vehicle } = useLayoutContext();
  const [contacts] = api.contact.list.useSuspenseQuery();
  const [typeOptions] = api.incomeAndExpenses.getTypeOptions.useSuspenseQuery({
    vehicleId,
  });

  const { control, watch, setValue } =
    useFormContext<rules.ReconcileFormValues>();

  const amountTcy = watch('bankFee.amountTcy') ?? 0;
  const vatRate = watch('bankFee.vatRate') ?? 0;
  const allocationRule = watch('bankFee.allocationRule');
  const allocations = watch('bankFee.allocations') ?? [];
  const currencyRate = watch('bankFee.currencyRate');

  const amountExVat = round(amountTcy / (1 + vatRate / 100), 2);

  const fundingSourceOptions = useFundingSourceOptions(vehicleId);

  const commitmentStatusOptions = [
    {
      value: ActionCommitmentStatus.InsideCommitment,
      label: 'Inside commitment',
    },
    {
      value: ActionCommitmentStatus.OutsideCommitment,
      label: 'Outside commitment',
    },
  ];

  const { isFetchingRate, fxRate, changeRate } = useCurrencyRate({
    vehicleId,
    currency,
    date,
    defaultCurrencyRate: currencyRate ?? null,
    onRateChange: (rate) => setValue('bankFee.currencyRate', rate ?? undefined),
  });

  const isMultiCurrency = vehicle && currency !== vehicle.baseCurrency;

  return (
    <Column spacing="lg">
      <Row centered justify="space-between">
        <Typography variant="h5">Add bank fee</Typography>
        <IconButton size="small" onClick={onRemove}>
          <Close fontSize="small" />
        </IconButton>
      </Row>
      <Row spacing="md" fullWidth>
        <FormField label="Expense type" size="md" spacing="xs">
          <Controller
            name="bankFee.customActionTypeId"
            control={control}
            render={({ field, fieldState }) => (
              <BasicSelect
                fullWidth
                options={typeOptions.expenseTypeOptions.map((opt) => ({
                  value: opt.customActionTypeId ?? opt.value,
                  label: opt.label,
                }))}
                value={field.value ?? ''}
                onChange={(value) => {
                  const selected = typeOptions.expenseTypeOptions.find(
                    (opt) => (opt.customActionTypeId ?? opt.value) === value,
                  );
                  field.onChange(value || null);
                  setValue(
                    'bankFee.customActionTypeName',
                    selected?.label ?? null,
                  );
                }}
                placeholder="Select expense type"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormField>
        <FormField
          label="Funding source"
          size="md"
          spacing="xs"
          tooltip="Select whether this action will be paid through general working capital on account or fresh capital that will be part of a future capital call."
        >
          <Controller
            name="bankFee.fundingSource"
            control={control}
            render={({ field, fieldState }) => (
              <BasicSelect
                fullWidth
                options={fundingSourceOptions.options}
                value={field.value ?? ''}
                onChange={(value) =>
                  field.onChange((value as FundingSource) || null)
                }
                disabled={fundingSourceOptions.isLoading}
                placeholder="Select funding source"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormField>
        <FormField label="Inside/outside commitment" size="md" spacing="xs">
          <Controller
            name="bankFee.commitmentStatus"
            control={control}
            render={({ field, fieldState }) => (
              <BasicSelect
                fullWidth
                options={commitmentStatusOptions}
                value={field.value ?? ''}
                onChange={(value) => {
                  const commitmentStatus =
                    (value as ActionCommitmentStatus) || null;
                  field.onChange(commitmentStatus);
                  if (
                    commitmentStatus ===
                    ActionCommitmentStatus.OutsideCommitment
                  ) {
                    setValue(
                      'bankFee.fundingSource',
                      FundingSource.CapitalCall,
                    );
                  }
                }}
                placeholder="Select inside/outside commitment"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormField>
        <FormField label="Counterparty" size="md" spacing="xs">
          <Controller
            name="bankFee.counterparty"
            control={control}
            render={({ field, fieldState }) => (
              <CounterpartySelector
                contacts={contacts}
                value={field.value ?? undefined}
                onChange={(value) => field.onChange(value ?? null)}
                placeholder="Select counterparty"
                error={
                  fieldState.error
                    ? { message: fieldState.error.message }
                    : undefined
                }
              />
            )}
          />
        </FormField>
      </Row>
      <Row spacing="md" fullWidth>
        <Column spacing="xs" fullWidth>
          <Typography variant="caption" color="textSecondary">
            Amount
          </Typography>
          <Controller
            name="bankFee.amountTcy"
            control={control}
            render={({ field, fieldState }) => (
              <NumberInput
                value={field.value}
                onChange={(amount) => field.onChange(amount ?? 0)}
                min={0}
                currency={currency}
                currencyStartAdornment
                decimals={2}
                fixedDecimalScale
                fullWidth
                clearZeroValueOnFocus
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Column>
        <StaticField label="Date">
          <Typography variant="body2">{date.toReadable()}</Typography>
        </StaticField>
      </Row>
      <Row spacing="md" fullWidth>
        <Column spacing="xs" fullWidth>
          <Typography variant="caption" color="textSecondary">
            VAT rate (%)
          </Typography>
          <Controller
            name="bankFee.vatRate"
            control={control}
            render={({ field, fieldState }) => (
              <VatRateSelector
                error={
                  fieldState.error
                    ? { message: fieldState.error.message }
                    : undefined
                }
                placeholder="Enter VAT rate"
                {...field}
              />
            )}
          />
          <Typography variant="caption" color="textSecondary">
            Amount ex VAT: {numberToCurrencyString(amountExVat, { currency })}
          </Typography>
        </Column>
        <Column spacing="xs" fullWidth>
          <Typography variant="caption" color="textSecondary">
            Description (optional)
          </Typography>
          <Controller
            name="bankFee.description"
            control={control}
            render={({ field }) => (
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
        </Column>
      </Row>
      {isMultiCurrency && vehicle && (
        <CurrencyRate
          currency={currency}
          date={date}
          vehicle={vehicle}
          fxRate={fxRate}
          isFetchingRate={isFetchingRate}
          onRateChange={changeRate}
        />
      )}
      <Column spacing="sm">
        <Typography variant="body2">
          <TypographyButton
            text="View allocation"
            textDecoration="none"
            onClick={() => setShowAllocations((prev) => !prev)}
            showExpandableStartAdornment
            isExpanded={showAllocations}
          />
        </Typography>
        {showAllocations && (
          <AllocationsManager
            vehicleId={vehicleId}
            allocations={allocations}
            allocatedActionLabel="Expense"
            allocatedAmountTcy={amountTcy}
            currency={currency}
            baseCurrency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
            allocationRule={allocationRule}
            onAllocationRuleChange={(rule) =>
              setValue('bankFee.allocationRule', rule)
            }
            onAllocationsChange={(allocs) =>
              setValue('bankFee.allocations', allocs)
            }
            date={date}
          />
        )}
      </Column>
    </Column>
  );
};

export default AddBankFeeForm;
